import useAuthStore from "@/store/authStore";
import { useEffect, useState, useMemo } from "react";
import { isPersonalProject } from "@/features/sidebar/projects/utils";

const EXPANDED_WORKSPACES_STORAGE_KEY = "sidebar_expanded_workspaces";

export function useProjects() {
  const {
    workspaces: rawWorkspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    setCurrentWorkspace,
    setWorkspaces,
  } = useAuthStore();

  // Sort workspaces to ensure Personal Project is always first
  const workspaces = useMemo(() => {
    if (!rawWorkspaces || rawWorkspaces.length === 0) return rawWorkspaces;

    return [...rawWorkspaces].sort((a, b) => {
      // Personal Project (name === "Personal Project" AND is_personal === true) always comes first
      const aIsPersonalProject = isPersonalProject(a);
      const bIsPersonalProject = isPersonalProject(b);

      if (aIsPersonalProject && !bIsPersonalProject) return -1;
      if (!aIsPersonalProject && bIsPersonalProject) return 1;
      // Otherwise maintain original order
      return 0;
    });
  }, [rawWorkspaces]);

  // Load expanded workspaces from localStorage on mount
  const [expandedWorkspaces, setExpandedWorkspaces] = useState(() => {
    try {
      const stored = localStorage.getItem(EXPANDED_WORKSPACES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [imageFallbacks, setImageFallbacks] = useState({});

  // Persist expandedWorkspaces to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        EXPANDED_WORKSPACES_STORAGE_KEY,
        JSON.stringify(expandedWorkspaces)
      );
    } catch (error) {
      console.error("Error saving expanded workspaces to localStorage:", error);
    }
  }, [expandedWorkspaces]);

  const handleImageError = (workspaceId) => {
    setImageFallbacks((prev) => ({
      ...prev,
      [workspaceId]: true,
    }));
  };

  // Reset image fallbacks when workspace image_path changes
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      setImageFallbacks((prev) => {
        const newFallbacks = { ...prev };
        let hasChanges = false;

        workspaces.forEach((workspace) => {
          if (
            workspace.is_team &&
            workspace.image_path &&
            newFallbacks[workspace.id]
          ) {
            delete newFallbacks[workspace.id];
            hasChanges = true;
          }
        });

        return hasChanges ? newFallbacks : prev;
      });
    }
  }, [workspaces]);

  const toggleWorkspaceExpansion = (e, workspaceId) => {
    e.stopPropagation();

    setExpandedWorkspaces((prev) => ({
      ...prev,
      [workspaceId]: !prev[workspaceId],
    }));
  };

  return {
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    setCurrentWorkspace,
    setWorkspaces,
    expandedWorkspaces,
    setExpandedWorkspaces,
    showAllProjects,
    setShowAllProjects,
    imageFallbacks,
    handleImageError,
    toggleWorkspaceExpansion,
  };
}
